import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Shield, MapPin, Clock, Star, Send, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

export default function PublicFeedbackPage() {
  const { code } = useParams();
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [reviewerType, setReviewerType] = useState('');
  const [uniformRating, setUniformRating] = useState(0);
  const [engagementRating, setEngagementRating] = useState(0);
  const [informationRating, setInformationRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [professionalismRating, setProfessionalismRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/feedback/${code}`);
        setEventInfo(response.data);
      } catch (err) {
        setError('Invalid or expired feedback code');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reviewerType) return;
    if (uniformRating === 0 || engagementRating === 0 || informationRating === 0 || communicationRating === 0 || professionalismRating === 0) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/${code}`, {
        reviewer_type: reviewerType,
        uniform_rating: uniformRating,
        engagement_rating: engagementRating,
        information_rating: informationRating,
        communication_rating: communicationRating,
        professionalism_rating: professionalismRating,
        comment: comment || null
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateRange = (dates) => {
    if (!dates || dates.length === 0) return '';
    const sorted = [...dates].sort();
    if (sorted.length === 1) {
      try { return format(parseISO(sorted[0]), 'EEEE, MMMM d, yyyy'); } catch { return sorted[0]; }
    }
    try {
      return `${format(parseISO(sorted[0]), 'MMM d')} - ${format(parseISO(sorted[sorted.length - 1]), 'MMM d, yyyy')}`;
    } catch { return `${sorted[0]} - ${sorted[sorted.length - 1]}`; }
  };

  const StarRating = ({ value, onChange, label, testId }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-white/80">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1 transition-transform hover:scale-110"
            data-testid={`${testId}-star-${star}`}
          >
            <Star
              className={cn(
                "w-8 h-8 transition-colors",
                star <= value ? "fill-yellow-400 text-yellow-400" : "text-white/20"
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="animate-pulse text-white/50">Loading...</div>
      </div>
    );
  }

  if (error && !eventInfo) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-bold text-white mb-2">Invalid Code</h2>
            <p className="text-white/50">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-400" />
            <h2 className="text-2xl font-bold text-white">Thank You!</h2>
            <p className="text-white/60">Your feedback has been submitted successfully.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full" data-testid="feedback-form-card">
        <CardContent className="p-6 space-y-6">
          {/* Header */}
          <div className="text-center space-y-3 pb-4 border-b border-zinc-800">
            <div className="flex items-center justify-center gap-2">
              <Shield className="w-6 h-6 text-yellow-400" />
              <h1 className="text-xl font-bold tracking-widest text-white uppercase">Security</h1>
            </div>
            <p className="text-lg font-medium text-yellow-400 tracking-widest uppercase">Personal Feedback</p>
          </div>

          {/* Event Info */}
          <div className="space-y-2 p-3 bg-zinc-800/50 rounded-lg">
            <p className="text-white font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-yellow-400" />
              {eventInfo.location}
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Reviewer Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-white/80">Who are you?</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setReviewerType('staff')}
                  className={cn(
                    "p-3 rounded-lg border text-sm font-medium transition-all",
                    reviewerType === 'staff'
                      ? "bg-yellow-400 text-black border-yellow-400"
                      : "bg-zinc-800 text-white/70 border-zinc-700 hover:border-yellow-400/50"
                  )}
                  data-testid="reviewer-staff-btn"
                >
                  Colleague / Staff
                </button>
                <button
                  type="button"
                  onClick={() => setReviewerType('public')}
                  className={cn(
                    "p-3 rounded-lg border text-sm font-medium transition-all",
                    reviewerType === 'public'
                      ? "bg-yellow-400 text-black border-yellow-400"
                      : "bg-zinc-800 text-white/70 border-zinc-700 hover:border-yellow-400/50"
                  )}
                  data-testid="reviewer-public-btn"
                >
                  Public / Customer
                </button>
              </div>
            </div>

            {/* Rating Sections */}
            <StarRating label="Uniform" value={uniformRating} onChange={setUniformRating} testId="uniform" />
            <StarRating label="Engagement" value={engagementRating} onChange={setEngagementRating} testId="engagement" />
            <StarRating label="Information Provided" value={informationRating} onChange={setInformationRating} testId="information" />
            <StarRating label="Communication" value={communicationRating} onChange={setCommunicationRating} testId="communication" />
            <StarRating label="Professionalism" value={professionalismRating} onChange={setProfessionalismRating} testId="professionalism" />

            {/* General Comment */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-white/80">General Comment</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Leave a comment (optional)..."
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-white/30 resize-none rounded-lg"
                rows={3}
                data-testid="feedback-comment"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={!reviewerType || uniformRating === 0 || engagementRating === 0 || informationRating === 0 || communicationRating === 0 || professionalismRating === 0 || submitting}
              className="w-full bg-yellow-400 text-black hover:bg-yellow-300 rounded-lg font-bold uppercase tracking-wider py-6"
              data-testid="submit-feedback-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
